import React, { forwardRef } from 'react';

import { isLocalURL } from '$utilities/browser';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @typedef {PropTypes.InferProps<typeof propTypes>} Props
 * @typedef {Object} ExtendedProps
 * @prop {('a'|'button'|'div')} [as=button] DOM element
 *
 * 1. Generates default attributes based on the button type and link location.
 */
const Button = forwardRef((
    /**
     * @type {Omit<Props, 'as' | 'look' | 'size'> & ExtendedProps}
     */
    {
        as: ComponentTag = 'button',
        look = 'inline',
        children,
        className = '',
        iconClassName = '',
        disabled,
        renderIcon,
        ...remainingProps
    },
    /**@type {React.ForwardedRef<(HTMLButtonElement | HTMLAnchorElement | HTMLDivElement)>} */
    ref
) => {
    const buttonClasses = ['transition-color'];
    const defaultAttributes = {};

    /* [1] */
    switch (ComponentTag) {
        case 'a':
            if (remainingProps.href) {
                buttonClasses.push('link-reset text-decoration-reset');

                if (!isLocalURL(remainingProps.href)) {
                    defaultAttributes.rel = 'noopener noreferrer';
                    defaultAttributes.target = '_blank';
                }
            } else {
                if(!remainingProps.href) {
                    console.warn('Missing href attribute on <Button> component');
                }
            }
            break;

        case 'button':
            buttonClasses.push('form-field-reset');
            defaultAttributes.type = 'button';
            break;
    }

    let iconClasses = '';
    switch (look) {
        case 'button':
        default:
            buttonClasses.push('button flex border-radius-100px');
            break;
        case 'fill':
            buttonClasses.push('button inline-flex width-100 justify-between pl5 md-pl6 pr6 pt6 pb6 md-py8');
            break;
        case 'fill-narrow':
            buttonClasses.push('button inline-flex width-100 justify-between pl5 md-pl6 pr6 pt6 pb6 md-py5');
            break;
        case 'underline':
            buttonClasses.push('inline-flex width-100 justify-between py5 md-py5 border-bottom black items-center');
            iconClasses += ' bg-white black button border-radius-100px p3 md-p5';
            break;
    }


    buttonClasses.push(className);

    if (disabled) {
        buttonClasses.push('muted');
    } else {
        buttonClasses.push('cursor-pointer');
    }

    return (
        <ComponentTag
            className={buttonClasses.join(' ')}
            disabled={disabled}
            {...defaultAttributes}
            {...remainingProps}
            ref={ref}>
            {children}

            {renderIcon && (
                <span
                    aria-hidden={true}
                    className={`font-0 line-height-0 ${iconClassName ? iconClassName : iconClasses}`}>
                    {renderIcon}
                </span>
            )}
        </ComponentTag>
    );
});

/* ========================================================================= */
/* Export(s) */
/* ========================================================================= */

Button.displayName = 'Button';

export default Button;
